import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, ChevronRight, UserPlus, RefreshCw, CalendarX, CalendarDays, MessageCircleQuestion, Mail } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatElapsedCompact, isRelativeDurationLabel } from '@/shared/lib/formatters';
import { AUTO_CLOSE_MS } from '@/shared/lib/constants';
import { useNotifications } from '@/hooks/suppliers';
import type { NotificationView as NotificationRow } from '@mondivago/types';

type NotificationType = NotificationRow['type'];

interface NotificationStyle {
  icon: typeof Bell;
  bg: string;
  color: string;
  ring: string;
  label: string;
  baseColor: string;
}

const NOTIFICATION_STYLES: Record<NotificationType, NotificationStyle> = {
  new_booking: {
    icon: UserPlus,
    bg: 'bg-lime-100',
    color: 'text-lime-800',
    ring: 'ring-lime-100',
    label: 'New booking',
    baseColor: 'lime-100',
  },
  booking_updated: {
    icon: RefreshCw,
    bg: 'bg-indigo-100',
    color: 'text-indigo-800',
    ring: 'ring-indigo-100',
    label: 'Booking updated',
    baseColor: 'indigo-100',
  },
  booking_cancelled: {
    icon: CalendarX,
    bg: 'bg-red-100',
    color: 'text-red-800',
    ring: 'ring-red-100',
    label: 'Booking cancelled',
    baseColor: 'red-100',
  },
  wallet_topup: {
    icon: RefreshCw,
    bg: 'bg-blue-100',
    color: 'text-blue-800',
    ring: 'ring-blue-100',
    label: 'Wallet top-up',
    baseColor: 'blue-100',
  },
  message_received: {
    icon: Mail,
    bg: 'bg-violet-100',
    color: 'text-violet-800',
    ring: 'ring-violet-100',
    label: 'Message received',
    baseColor: 'violet-100',
  },
  new_question: {
    icon: MessageCircleQuestion,
    bg: 'bg-amber-100',
    color: 'text-amber-800',
    ring: 'ring-amber-100',
    label: 'New question',
    baseColor: 'amber-100',
  },
};

export function NotificationDropdown({ buttonClassName }: { buttonClassName?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, markAllAsRead, markAsRead } = useNotifications();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, AUTO_CLOSE_MS);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleNotificationClick = async (notification: NotificationRow) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    setIsOpen(false);

    if (notification.booking_id) {
      void navigate(`/supplier/bookings?bookingId=${notification.booking_id}`);
    } else if (notification.type === 'wallet_topup') {
      void navigate('/supplier/wallet');
    } else if (notification.type === 'new_question') {
      void navigate('/supplier/messages?tab=questions');
    } else {
      void navigate('/supplier/dashboard');
    }
  };

  return (
    <div className="relative" ref={containerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className={
          buttonClassName ??
          'relative rounded-full p-2 text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600 active:scale-95'
        }
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-lime-900 px-1.5 text-[10px] font-black text-white absolute -right-1.5 -top-1 shadow-lg shadow-lime-900/20">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-[100] mt-1 w-80 sm:w-[360px] origin-top-right overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300">
          <div className="flex flex-col max-h-[60vh]">
            {/* Header */}
            <div className="px-4 pt-4 pb-3 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="heading-3">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={() => void markAllAsRead()}
                    className="eyebrow text-gray-400 hover:text-gray-900 transition-colors active:scale-95"
                  >
                    Mark all as read
                  </button>
                )}
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-1.5 pb-4 custom-scrollbar">
              {notifications.length === 0 ? (
                <div className="py-12">
                  <EmptyState
                    icon={BellOff}
                    title="No notifications"
                    description="Your activity will appear here in real time."
                    size="sm"
                  />
                </div>
              ) : (
                <div className="space-y-0">
                  {notifications.map((notif) => {
                    const style = NOTIFICATION_STYLES[notif.type];
                    const IconComponent = style?.icon ?? Bell;
                    const elapsed = formatElapsedCompact(notif.created_at);
                    const elapsedLabel = isRelativeDurationLabel(elapsed) ? `${elapsed} ago` : elapsed;
                    const isBooking = notif.type.includes('booking');

                    return (
                      <div
                        key={notif.id}
                        onClick={() => void handleNotificationClick(notif)}
                        className={`group relative flex gap-3 rounded-2xl p-4 transition-all duration-200 cursor-pointer active:scale-[0.99] hover:bg-gray-50`}
                      >
                        {/* Unread indicator */}
                        {!notif.is_read && (
                          <div className="absolute left-2.5 top-5 h-1.5 w-1.5 rounded-full bg-gray-900 ring-2 ring-white" />
                        )}

                        {/* Type-specific icon */}
                        <div className="mt-0.5 shrink-0">
                          <div
                            className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                              style
                                ? `${style.bg} ${style.color} ring-1 ${style.ring}`
                                : 'bg-gray-100 text-gray-500 ring-1 ring-gray-200'
                            }`}
                          >
                            <IconComponent className="h-3.5 w-3.5" />
                          </div>
                        </div>

                        <div className="flex-1 min-w-0 pt-0.5">
                          {/* Title row */}
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className={`truncate text-sm leading-tight ${!notif.is_read ? 'font-black text-gray-900' : 'font-bold text-gray-700'}`}
                            >
                              {notif.title}
                            </p>
                            <span className="shrink-0 mt-0.5 meta text-gray-400 tabular-nums whitespace-nowrap">
                              {elapsedLabel}
                            </span>
                          </div>

                          {/* Message preview */}
                          <p className="mt-1 desc-sm line-clamp-2 leading-relaxed">{notif.message}</p>

                          {/* Booking detail card */}
                          {isBooking && (
                            <div className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-gray-100 bg-white px-2.5 py-1.5 shadow-sm transition-all duration-200 group-hover:border-gray-200 group-hover:shadow-md w-fit max-w-[210px] sm:max-w-[230px]">
                              <div className="flex items-center gap-2 overflow-hidden min-w-0">
                                <CalendarDays className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                <span className="truncate meta !font-bold !text-gray-600">
                                  {notif.payload?.travelerName ?? notif.payload?.travelName ?? 'Booking details'}
                                </span>
                              </div>
                              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-500" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
